import React from 'react'
import classNames from 'classnames'
import { getInputBaseClasses, getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface PriceInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value?: number
  onChange?: (value: number | undefined) => void
  label?: string
  error?: boolean
  helperText?: string
}

/**
 * Componente de input de preço com formatação automática de moeda brasileira
 */
const PriceInput = React.forwardRef<HTMLInputElement, PriceInputProps>(
  ({ value, onChange, label, error, helperText, className = '', disabled, placeholder, id, ...props }, ref) => {
    const inputClasses = getInputBaseClasses(error, disabled, false, className)
    
    const formatter = React.useMemo(
      () => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }),
      []
    )

    const formatCurrency = React.useCallback(
      (val?: number) => (val === undefined || val === null ? '' : formatter.format(val)),
      [formatter]
    )

    const parseCurrency = React.useCallback((raw: string): number | undefined => {
      const digits = raw.replace(/\D/g, '')
      if (!digits) {
        return undefined
      }
      const cents = Number(digits)
      if (!Number.isFinite(cents)) {
        return undefined
      }
      return cents / 100
    }, [])

    const [displayValue, setDisplayValue] = React.useState<string>(() => formatCurrency(value))

    React.useEffect(() => {
      setDisplayValue(formatCurrency(value))
    }, [value, formatCurrency])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseCurrency(event.target.value)
      onChange?.(next)
      setDisplayValue(next === undefined ? '' : formatCurrency(next))
    }

    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={classNames(inputClasses, 'mt-0')}
          placeholder={placeholder || 'R$ 0,00'}
          {...props}
        />
        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

PriceInput.displayName = 'PriceInput'

export default PriceInput

